/**
 * Venta de Productos / Vehículos — migración web de ares2010 (escritorio).
 *
 * Flujo: buscar cliente por cédula (foto, balance pendiente, datos de la última
 * solicitud), validar chasis disponible, calcular cuotas/deuda según la forma
 * de pago y guardar (factmot + solicit + prestamo + amort en el backend).
 * Si la operación requiere autorización (interés fuera de rango, inicial bajo
 * el límite o cliente con balance pendiente) se solicita vía la tabla de
 * autorizaciones y se hace polling hasta que la aprueben o declinen.
 */
import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Avatar from "@mui/material/Avatar";
import InputAdornment from "@mui/material/InputAdornment";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import CircularProgress from "@mui/material/CircularProgress";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import SaveIcon from "@mui/icons-material/Save";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import SellOutlinedIcon from "@mui/icons-material/SellOutlined";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DirectionsCarFilledOutlinedIcon from "@mui/icons-material/DirectionsCarFilledOutlined";
import AssignmentIndOutlinedIcon from "@mui/icons-material/AssignmentIndOutlined";
import AddAPhotoOutlinedIcon from "@mui/icons-material/AddAPhotoOutlined";
import GppMaybeOutlinedIcon from "@mui/icons-material/GppMaybeOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import { useGlobalMDAlert, useMaterialUIController } from "context";
import { FP, PageHeader, DocumentoField, fpInputSx } from "components/financepro";
import {
  useFormularioExtra,
  CamposExtraForm,
  validarCamposExtra,
  seccionesCodeudor,
  usePersonaExtra,
  completarVacios,
} from "components/camposextra";
import { documentoValido, tipoDocumento } from "lib/documento";
import AlertaRed from "modules/dataorbit/alertaventa";
import AlertaCodeudor from "components/codeudor/AlertaCodeudor";
import { tienePermisosModulo } from "lib/seguridad/permisosPorModulo";
import { useTiposDocumento, labelTipo } from "lib/documentosvehiculo";
import { useApi } from "lib/api";
import { calcularCuotas, useConfigCuotas, mananaISO, unMesISO } from "lib/calculocuotas";
import CampoCuota from "components/cuotas/CampoCuota";
import { imprimirFacturaMotor } from "modules/facturacion/impresionfacturamotor";
import { PlanCompletivoEditor } from "components/completivos/PlanCompletivo";
import { validarPlanCompletivo, normalizarPlan } from "lib/completivoplan";
import { useBorradorVenta } from "lib/borradorventa";
import AvisoBorradorVenta from "components/borradorventa/AvisoBorradorVenta";
import { CobroInicialDialog } from "modules/facturacion/iniciales";

const FORM_INICIAL = {
  // Datos generales
  cedula: "",
  nombres: "",
  apellidos: "",
  direccion: "",
  provincia: "",
  telefono: "",
  celular: "",
  email: "",
  sexo: "Femenino",
  ecivil: "Soltero",
  depend: "0",
  nacionalidad: "Dominicano",
  edad: "",
  apodo: "",
  // Datos del préstamo
  comentario: "",
  // Vendedor (usuario del sistema): se pide solo con usavendedor = S en la
  // configuración; se prellena con el de la solicitud de crédito si existe.
  vendedor: "",
  chasis: "",
  marca: "",
  modelo: "",
  colore: "",
  precio: "",
  inicial: "",
  // Inicial pactado: si supera lo cobrado, la venta financia como si el
  // inicial estuviera completo y la diferencia queda como completivo por
  // cobrar (se cobra luego en la pantalla de pagos).
  inicialpactado: "",
  // Plan del completivo: partes con fecha [{ fecha, monto }]. Vacío = todo
  // el completivo vence el día de la venta (ver lib/completivoplan).
  completivoplan: [],
  financia: "",
  plazo: "",
  formadepago: "",
  // Calendario de cuotas: en Dias, no crear cuotas en domingo; en Quincenas,
  // días fijos del mes (ej. 15 y 30). Vacíos = cada 15 días corridos.
  excluirdomingos: false,
  diaquincena1: "",
  diaquincena2: "",
  // Solo con varinicial.elegirprimeracuota; vacía = calendario automático.
  fechaprimeracuota: "",
  interes: "",
  valorcuota: "",
  deudatotal: "",
  // Fiador
  fiador: "",
  fiadorced: "",
  fiadortel: "",
  fiadordir: "",
};

// Filtro local del chasis: matchea por chasis, marca, modelo o color.
const filtrarChasis = createFilterOptions({
  stringify: (o) =>
    typeof o === "string" ? o : `${o.chasis} ${o.marca || ""} ${o.modelo || ""} ${o.colore || ""}`,
  limit: 60,
});

const fmtMoney = (v) => {
  const n = typeof v === "number" ? v : parseFloat(String(v || "").replace(/,/g, ""));
  if (Number.isNaN(n)) return "0.00";
  return n.toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const toNum = (v) => {
  const n = parseFloat(String(v ?? "").replace(/,/g, ""));
  return Number.isNaN(n) ? 0 : n;
};

// Reduce la foto a 80x80 JPEG (igual que el escritorio) y la devuelve en base64.
function comprimirFoto(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = 80;
      canvas.height = 80;
      canvas.getContext("2d").drawImage(img, 0, 0, 80, 80);
      URL.revokeObjectURL(url);
      const dataUrl = canvas.toDataURL("image/jpeg", 0.7);
      resolve(dataUrl.split(",")[1] || "");
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Foto inválida"));
    };
    img.src = url;
  });
}

const btnBase = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };
const btnSolid = (bg, hover) => ({
  ...btnBase,
  bgcolor: bg,
  color: "#FFFFFF",
  "&:hover": { bgcolor: hover, boxShadow: "none", color: "#FFFFFF" },
  "&.Mui-disabled": { bgcolor: `${bg}66`, color: "#FFFFFF" },
});
const btnOutline = (c) => ({
  ...btnBase,
  color: c,
  bgcolor: "#FFFFFF",
  border: `1px solid ${c}`,
  "&:hover": { bgcolor: `${c}12`, border: `1px solid ${c}`, color: c },
});
const btnGhost = {
  ...btnBase,
  color: FP.color.textSecondary,
  bgcolor: "transparent",
  "&:hover": { bgcolor: FP.color.surfaceAlt, color: FP.color.textPrimary },
};

const sectionBox = {
  border: `1px solid ${FP.color.border}`,
  borderRadius: 2.5,
  p: { xs: 2, sm: 2.6 },
  mb: 2.4,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
};

function SectionTitle({ icon: Icon, children, color }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 2 }}>
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: 1.4,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: `${color}14`,
          color,
        }}
      >
        <Icon sx={{ fontSize: 19 }} />
      </Box>
      <Typography
        sx={{
          fontWeight: 800,
          fontSize: "0.74rem",
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          color: FP.color.textSecondary,
        }}
      >
        {children}
      </Typography>
    </Box>
  );
}

export default function VentaProductos({
  embedded = false,
  onExit,
  onSaved,
  restaurarBorrador = false,
} = {}) {
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();
  const { redondeo, elegirPrimeraCuota } = useConfigCuotas();

  const [tab, setTab] = useState(0);
  const [form, setForm] = useState(FORM_INICIAL);
  const setF = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));
  // Tipo de documento (cedula/rnc/pasaporte) del cliente y del fiador: solo
  // controla máscara y validación; en la base va en la misma columna cedula.
  const [tipodoc, setTipodoc] = useState("cedula");
  const [tipodocFiador, setTipodocFiador] = useState("cedula");

  // Configuración del tenant (provincias, formas de pago, límites).
  const [config, setConfig] = useState(null);
  useEffect(() => {
    let activo = true;
    post("/api/ventaproducto_configuracion", {}).then(({ ok, data }) => {
      if (!activo) return;
      if (ok) setConfig(data);
      else
        showAlert({ message: data.Error || "No se pudo cargar la configuración", color: "error" });
    });
    return () => {
      activo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const provincias = config?.provincias || [];
  const usaVendedor = config?.usavendedor === "S";

  // Usuarios del sistema para el selector de vendedor (mismo endpoint que
  // la solicitud de crédito). Solo se cargan si el tenant usa vendedor.
  const [usuarios, setUsuarios] = useState([]);
  useEffect(() => {
    if (!usaVendedor) return undefined;
    let activo = true;
    post("/api/solicitudcredito_usuarios", {}).then(({ ok, data }) => {
      if (activo && ok && Array.isArray(data.usuarios)) setUsuarios(data.usuarios);
    });
    return () => {
      activo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usaVendedor]);
  const formasdepago = config?.formasdepago || ["Meses", "Quincenas", "Semanas", "Dias"];

  // ---- Ventas adicionales (GPS, seguros, etc.) ----
  // Se registran contra el préstamo recién creado con distribucion activa:
  // su valor se suma al capital de las cuotas y a la deuda del préstamo. La
  // sección aparece solo si el tenant asocia ventas a préstamos
  // (asociarprestamogps='S') y el rol tiene ares9610; si no, se oculta.
  const [ventasConfig, setVentasConfig] = useState(null);
  useEffect(() => {
    let activo = true;
    post("/api/ventageneral_config", {}).then(({ ok, data }) => {
      if (!activo) return;
      if (ok && data.asociarprestamo === "S" && (data.categorias || []).length) {
        setVentasConfig(data);
      }
    });
    return () => {
      activo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [extras, setExtras] = useState([]);
  const [extrasOpen, setExtrasOpen] = useState(false);
  const [extraCat, setExtraCat] = useState("");
  const [extraValor, setExtraValor] = useState("");
  const [extraGps, setExtraGps] = useState("");
  // En cuántas cuotas se reparte cada ítem (vacío = todo el plazo).
  const [extraCuotas, setExtraCuotas] = useState("");

  const extraGpsObligatorio = (ventasConfig?.categorias || []).some(
    (c) => c.categoria === extraCat && c.gpsobligatorio === "S"
  );

  const agregarExtra = () => {
    if (!extraCat) {
      showAlert({ message: "Indique la categoría de la venta adicional", color: "warning" });
      return;
    }
    if (toNum(extraValor) <= 0) {
      showAlert({ message: "El valor de la venta adicional no puede ser 0", color: "warning" });
      return;
    }
    if (extraGpsObligatorio && !extraGps.trim()) {
      showAlert({
        message: `La categoría ${extraCat} requiere el número de GPS`,
        color: "warning",
      });
      return;
    }
    setExtras((p) => [
      ...p,
      {
        tipogasto: extraCat,
        valor: toNum(extraValor),
        gps: extraGps.trim(),
        cuotas: toNum(extraCuotas), // 0 = todo el plazo
      },
    ]);
    setExtraCat("");
    setExtraValor("");
    setExtraGps("");
    setExtraCuotas("");
  };

  const eliminarExtra = (idx) => setExtras((p) => p.filter((_, i) => i !== idx));

  const totalExtras = extras.reduce((s, ex) => s + ex.valor, 0);
  // Cuotas efectivas de un ítem: lo pedido, topado al plazo (0 = todo el plazo).
  const cuotasDe = (ex) => {
    const plazo = toNum(form.plazo);
    const pedidas = toNum(ex.cuotas);
    return pedidas > 0 && (plazo === 0 || pedidas <= plazo) ? pedidas : plazo;
  };

  // ---- Costo de documentos (matrícula, placa...) financiado en cuotas ----
  // Mismo mecanismo que las ventas adicionales, pero registrado como cobro
  // de documento (cobromatricula_guardar financiado): queda en Matrículas
  // desde el día uno, distribuido en las cuotas del préstamo. Visible solo
  // con el permiso de cobro (ares6010) — el backend re-valida.
  const [controller] = useMaterialUIController();
  const { roleDetalles } = controller;
  const puedeDocumentos = tienePermisosModulo(roleDetalles, "ares6010");
  const { tipos: tiposDocumento } = useTiposDocumento();

  const [documentos, setDocumentos] = useState([]);
  const [docsOpen, setDocsOpen] = useState(false);
  const [docTipo, setDocTipo] = useState("");
  const [docValor, setDocValor] = useState("");
  const [docCuotas, setDocCuotas] = useState("");
  // Tarifa propuesta al elegir tipo: solo si el valor está vacío o todavía
  // tiene la propuesta anterior — nunca pisa un monto tecleado.
  const [docTarifaPropuesta, setDocTarifaPropuesta] = useState(0);

  const elegirDocTipo = (value) => {
    setDocTipo(value);
    const tarifa = tiposDocumento.find((t) => t.value === value)?.precio || 0;
    if (docValor === "" || toNum(docValor) === docTarifaPropuesta) {
      setDocValor(tarifa > 0 ? String(tarifa) : "");
      setDocTarifaPropuesta(tarifa);
    }
  };

  const agregarDocumento = () => {
    if (!docTipo) {
      showAlert({ message: "Indique el documento a cobrar", color: "warning" });
      return;
    }
    if (toNum(docValor) <= 0) {
      showAlert({ message: "El valor del documento no puede ser 0", color: "warning" });
      return;
    }
    setDocumentos((p) => [
      ...p,
      { tipo: docTipo, valor: toNum(docValor), cuotas: toNum(docCuotas) }, // 0 = todo el plazo
    ]);
    setDocTipo("");
    setDocValor("");
    setDocCuotas("");
    setDocTarifaPropuesta(0);
  };

  const eliminarDocumento = (idx) => setDocumentos((p) => p.filter((_, i) => i !== idx));

  const totalDocumentos = documentos.reduce((s, d) => s + d.valor, 0);

  // ---- Cliente ----
  const [foto, setFoto] = useState(""); // base64 sin prefijo
  const [pendiente, setPendiente] = useState(0);
  const fotoInputRef = useRef(null);
  // Campos extra configurables (Configuración → Campos Adicionales); se
  // guardan asociados a la factura (origen 'factura'). Cada sección declara
  // en qué pestaña sale (tabVenta): "generales" o "prestamo" (el default,
  // también para configuraciones guardadas antes del selector).
  const seccionesExtra = useFormularioExtra();
  const seccionesExtraGenerales = seccionesExtra.filter((s) => s.tabVenta === "generales");
  const seccionesExtraPrestamo = seccionesExtra.filter((s) => s.tabVenta !== "generales");
  const [datosExtra, setDatosExtra] = useState({});
  // Secciones de persona que se capturan también para el fiador (origen
  // 'factura_cod'). La ficha por cédula precarga a quien ya vino antes,
  // como cliente o como fiador.
  const seccionesExtraCod = seccionesCodeudor(seccionesExtra);
  const [datosExtraCod, setDatosExtraCod] = useState({});
  const cargarPersonaExtra = usePersonaExtra();

  const seleccionarFoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      setFoto(await comprimirFoto(file));
    } catch {
      showAlert({ message: "Existe un error en la foto. Revise", color: "error" });
    }
  };

  // Solicitud de crédito vigente cargada por buscarPorCedula (o null). El
  // inicial pactado se digita solo cuando NO hay solicitud para el chasis
  // seleccionado; con solicitud manda ella (el backend la relee al guardar).
  const [solicitudCredito, setSolicitudCredito] = useState(null);
  const normChasis = (c) =>
    String(c || "")
      .trim()
      .toUpperCase();
  const pactadoDeSolicitud =
    !!solicitudCredito &&
    !!form.chasis &&
    normChasis(solicitudCredito.chasis) === normChasis(form.chasis);
  let pactadoHelper = pactadoDeSolicitud
    ? `Se toma de la solicitud de crédito #${solicitudCredito.nosolic}`
    : "Sin solicitud de crédito: digítelo si el cliente pactó un inicial mayor al cobrado";
  if (toNum(form.inicialpactado) > toNum(form.inicial)) {
    pactadoHelper = `Completivo por cobrar: RD$ ${fmtMoney(
      toNum(form.inicialpactado) - toNum(form.inicial)
    )} (se cobra en Pagos)`;
  }
  // Completivo por cobrar de esta venta (mismo criterio que el backend): se
  // reparte en el plan de fechas solo con la metodología activada.
  const completivoVenta =
    config?.completivoinicial === "S"
      ? Math.max(0, Math.round((toNum(form.inicialpactado) - toNum(form.inicial)) * 100) / 100)
      : 0;
  let pactadoValor = form.inicialpactado;
  if (pactadoDeSolicitud)
    pactadoValor = form.inicialpactado ? fmtMoney(form.inicialpactado) : "0.00";

  const buscarPorCedula = useCallback(async () => {
    const ced = form.cedula.trim();
    if (!documentoValido(ced, tipodoc)) return;
    const { ok, data } = await post("/api/ventaproducto_buscarcliente", { cedula: ced });
    if (!ok) return;
    setFoto(data.foto || "");
    setPendiente(toNum(data.pendiente));
    setSolicitudCredito(data.solicitudcredito || null);
    if (data.solicitud) {
      const s = data.solicitud;
      setForm((p) => ({
        ...p,
        nombres: s.nombres || p.nombres,
        apellidos: s.apellidos || p.apellidos,
        direccion: s.direccion || p.direccion,
        telefono: s.telefono || p.telefono,
        celular: s.celular || p.celular,
        provincia: s.urb || p.provincia,
        edad: s.edad != null ? String(s.edad) : p.edad,
        apodo: s.apodo || p.apodo,
        comentario: s.comentario || p.comentario,
        email: s.email || p.email,
      }));
    }
    // Solicitud de crédito vigente: prellena chasis y condiciones pactadas.
    if (data.solicitudcredito) await aplicarSolicitudCredito(data.solicitudcredito);
    // Ficha de la persona: completa los campos que la solicitud no trajo
    // (lo de la solicitud y lo ya digitado ganan).
    const ficha = await cargarPersonaExtra(ced, { cliente: true });
    setDatosExtra((p) => completarVacios(p, ficha));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.cedula, tipodoc, post]);

  // Datos generales del fiador por documento (su solicitud como deudor, el
  // registro de clientes o la última vez que fue fiador): solo llena los
  // campos vacíos, lo ya digitado no se pisa.
  const buscarFiadorGenerales = async () => {
    const ced = form.fiadorced.trim();
    if (!documentoValido(ced, tipodocFiador)) return;
    const { ok, data } = await post("/api/ventaproducto_buscarfiador", { cedula: ced });
    const f = ok ? data.fiador : null;
    if (!f) return;
    const vacio = (v) => !String(v ?? "").trim();
    setForm((p) => ({
      ...p,
      fiador: vacio(p.fiador) ? f.nombre || "" : p.fiador,
      fiadortel: vacio(p.fiadortel) ? f.telefono || "" : p.fiadortel,
      fiadordir: vacio(p.fiadordir) ? f.direccion || "" : p.fiadordir,
    }));
  };

  // Campos de persona del fiador: lo último capturado para su documento.
  const buscarFiadorExtra = async (cedFiador = form.fiadorced) => {
    const ced = String(cedFiador || "").trim();
    if (!seccionesExtraCod.length || !ced) return;
    const ficha = await cargarPersonaExtra(ced);
    setDatosExtraCod((p) => completarVacios(p, ficha));
  };

  // ---- Chasis ----
  // Se cargan todos los chasis disponibles de la sucursal una sola vez;
  // el Autocomplete filtra localmente mientras se escribe.
  const [chasisOpts, setChasisOpts] = useState([]);
  const [loadingChasis, setLoadingChasis] = useState(false);
  const [chasisValidado, setChasisValidado] = useState(false);
  // Abonos al inicial del chasis (puede haber varios; el campo muestra la suma).
  const [abonosInicial, setAbonosInicial] = useState([]);

  const cargarChasis = useCallback(async () => {
    setLoadingChasis(true);
    try {
      const { ok, data } = await post("/api/ventaproducto_chasis", {});
      if (ok && Array.isArray(data.chasis)) setChasisOpts(data.chasis);
    } finally {
      setLoadingChasis(false);
    }
  }, [post]);

  useEffect(() => {
    cargarChasis();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validarChasis = useCallback(
    async (chasis) => {
      if (!chasis) return;
      const { ok, data } = await post("/api/ventaproducto_validarchasis", { chasis });
      if (!ok) {
        showAlert({ message: data.Error || "No se pudo validar el chasis", color: "error" });
        setChasisValidado(false);
        setAbonosInicial([]);
        setForm((p) => ({
          ...p,
          chasis: "",
          marca: "",
          modelo: "",
          colore: "",
          precio: "",
          inicial: "",
          inicialpactado: "",
          completivoplan: [],
          financia: "",
        }));
        return;
      }
      const inicial = toNum(data.inicial?.valor);
      const precio = toNum(data.preciobase);
      setChasisValidado(true);
      setAbonosInicial(Array.isArray(data.inicial?.abonos) ? data.inicial.abonos : []);
      setForm((p) => ({
        ...p,
        chasis: data.chasis,
        marca: data.marca,
        modelo: data.modelo,
        colore: data.colore,
        precio: precio ? String(precio) : "",
        inicial: inicial ? String(inicial) : "",
        inicialpactado: "",
        completivoplan: [],
        financia: precio ? (precio - inicial).toFixed(2) : "",
        // Cambiar de chasis invalida el cálculo anterior (como el escritorio).
        plazo: "",
        formadepago: "",
        excluirdomingos: false,
        diaquincena1: "",
        diaquincena2: "",
        fechaprimeracuota: "",
        interes: "",
        valorcuota: "",
        deudatotal: "",
      }));
      if (inicial > 0 && precio - inicial <= 0) {
        showAlert({
          message:
            "Revise el inicial. El inicial es igual o mayor que el costo del producto/vehículo",
          color: "warning",
        });
      }
    },
    [post, showAlert]
  );

  // Financia y cuotas se recalculan al cambiar precio/plazo/forma/interés.
  // Con inicial pactado mayor que lo cobrado, financia usa el pactado (las
  // cuotas salen como si el inicial estuviera completo) y la diferencia es
  // el completivo por cobrar — mismo criterio que el backend.
  const recalcular = (next) => {
    const inicialAplicado = Math.max(toNum(next.inicial), toNum(next.inicialpactado));
    const financia = toNum(next.precio) - inicialAplicado;
    const r = calcularCuotas(
      financia,
      toNum(next.plazo),
      next.formadepago,
      toNum(next.interes),
      redondeo
    );
    return {
      ...next,
      financia: toNum(next.precio) > 0 ? financia.toFixed(2) : "",
      valorcuota: r ? r.valorcuota.toFixed(2) : "",
      deudatotal: r ? r.deudatotal.toFixed(2) : "",
    };
  };
  const setCalc = (k) => (e) => setForm((p) => recalcular({ ...p, [k]: e.target.value }));
  // El redondeo llega después del primer render: recalcula lo ya digitado.
  useEffect(() => {
    setForm((p) => recalcular(p));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [redondeo]);

  // Prellena la venta desde una solicitud de crédito vigente (buscarcliente la
  // devuelve en la clave solicitudcredito). Declarada como function para que
  // buscarPorCedula (definida arriba) pueda llamarla. El orden importa:
  // validarChasis PRIMERO (su setForm resetea plazo/forma/interés), y después
  // las condiciones pactadas pasando por recalcular.
  async function aplicarSolicitudCredito(sc) {
    if (sc.chasis) await validarChasis(sc.chasis);
    setForm((p) =>
      recalcular({
        ...p,
        nombres: p.nombres || sc.nombres || "",
        apellidos: p.apellidos || sc.apellidos || "",
        direccion: p.direccion || sc.direccion || "",
        telefono: p.telefono || sc.telefono || "",
        celular: p.celular || sc.celular || "",
        email: p.email || sc.email || "",
        edad: p.edad || (sc.edad != null ? String(sc.edad) : ""),
        comentario: p.comentario || sc.comentario || "",
        vendedor: p.vendedor || sc.vendedor || "",
        precio: sc.precio ? String(sc.precio) : p.precio,
        // Inicial pactado en la solicitud: si aún no está completo, la venta
        // financia con él y la diferencia queda como completivo. Solo con la
        // metodología activada en configuración (si no, el backend lo ignora
        // y aquí tampoco debe entrar al cálculo).
        inicialpactado:
          config?.completivoinicial === "S" && sc.inicial ? String(sc.inicial) : p.inicialpactado,
        interes: sc.interes ? String(sc.interes) : p.interes,
        plazo: sc.plazo ? String(sc.plazo) : p.plazo,
        formadepago: sc.periodos || p.formadepago,
        fiador: sc.fiador || p.fiador,
        fiadorced: sc.fiadorced || p.fiadorced,
        fiadortel: sc.fiadortel || p.fiadortel,
        fiadordir: sc.fiadordir || p.fiadordir,
      })
    );
    if (sc.fiadorced) setTipodocFiador(tipoDocumento(sc.fiadorced));
    // Campos extra capturados en la solicitud: se precargan editables (lo ya
    // digitado en esta pantalla no se pisa) y se guardan con la factura.
    if (sc.datosextra && typeof sc.datosextra === "object") {
      setDatosExtra((p) => ({ ...sc.datosextra, ...p }));
    }
    // Los del fiador capturados en la solicitud, y lo que falte, de su ficha.
    if (sc.datosextracodeudor && typeof sc.datosextracodeudor === "object") {
      setDatosExtraCod((p) => completarVacios(p, sc.datosextracodeudor));
    }
    if (sc.fiadorced) buscarFiadorExtra(sc.fiadorced);
    showAlert({ message: `Solicitud de crédito #${sc.nosolic} cargada`, color: "info" });
  }

  // ---- Autorización ----
  const [autOpen, setAutOpen] = useState(false);
  const [autMotivo, setAutMotivo] = useState("");
  const [autDescripcion, setAutDescripcion] = useState("");
  const [autIdtext, setAutIdtext] = useState("");
  const [autStatus, setAutStatus] = useState(""); // "" | Progreso | Aprobado | Cancelado
  const [autComentario, setAutComentario] = useState("");
  const pollRef = useRef(null);

  const detenerPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };
  useEffect(() => detenerPolling, []);

  // Espera la respuesta del administrador (también al retomar un borrador
  // que quedó con la autorización en curso).
  function iniciarPolling(idtext) {
    detenerPolling();
    pollRef.current = setInterval(async () => {
      const r = await post("/api/ventaproducto_estadoautorizacion", { idtext });
      if (!r.ok) return;
      const st = r.data.status;
      if (st === "Aprobado") {
        detenerPolling();
        setAutStatus("Aprobado");
      } else if (st === "Cancelado") {
        detenerPolling();
        setAutStatus("Cancelado");
        setAutComentario(r.data.comentario || "");
      }
    }, 5000);
  }

  const solicitarAutorizacion = async () => {
    const { ok, data } = await post("/api/ventaproducto_solicitarautorizacion", {
      descripcion: autDescripcion,
      datos: {
        cedula: form.cedula,
        nombre: form.nombres,
        apellido: form.apellidos,
        edad: form.edad,
        chasis: form.chasis,
        marca: form.marca,
        modelo: form.modelo,
        color: form.colore,
        precio: form.precio,
        financia: form.financia,
        plazo: form.plazo,
        formadepago: form.formadepago,
        interes: form.interes,
        valorcuota: form.valorcuota,
        deudatotal: form.deudatotal,
        inicial: form.inicial,
      },
    });
    if (!ok) {
      showAlert({ message: data.Error || "No se pudo solicitar la autorización", color: "error" });
      return;
    }
    setAutIdtext(data.idtext);
    setAutStatus("Progreso");
    iniciarPolling(data.idtext);
  };

  const cancelarAutorizacion = async () => {
    detenerPolling();
    if (autIdtext && autStatus === "Progreso") {
      await post("/api/ventaproducto_cancelarautorizacion", { idtext: autIdtext });
    }
    setAutOpen(false);
    setAutIdtext("");
    setAutStatus("");
    setAutComentario("");
  };

  // ---- Guardar ----
  const [guardando, setGuardando] = useState(false);

  const limpiarPantalla = () => {
    detenerPolling();
    setForm(FORM_INICIAL);
    setDatosExtra({});
    setDatosExtraCod({});
    setTipodoc("cedula");
    setTipodocFiador("cedula");
    setFoto("");
    setPendiente(0);
    setChasisValidado(false);
    setSolicitudCredito(null);
    setAutIdtext("");
    setAutStatus("");
    setAutComentario("");
    setExtras([]);
    setExtraCat("");
    setExtraValor("");
    setExtraGps("");
    setExtraCuotas("");
    setDocumentos([]);
    setDocTipo("");
    setDocValor("");
    setDocCuotas("");
    setDocTarifaPropuesta(0);
    setTab(0);
  };

  // Registra las ventas adicionales contra el préstamo recién creado. La
  // venta del vehículo ya está guardada: un fallo aquí solo se avisa (se
  // pueden registrar después desde Ventas Generales con el préstamo).
  const registrarExtras = async (noprest) => {
    const fallidos = [];
    for (const ex of extras) {
      try {
        // Secuencial a propósito: el backend abre una conexión por request.
        // eslint-disable-next-line no-await-in-loop
        const { ok, data } = await post("/api/ventageneral_guardar", {
          nombres: `${form.nombres} ${form.apellidos}`.trim(),
          cedula: form.cedula,
          gps: ex.gps,
          descripcion: `Venta a crédito préstamo #${noprest}`,
          tipogasto: ex.tipogasto,
          tipofactura: "Credito",
          noprest,
          valor: ex.valor,
          tipodepago: "",
          referenciapago: "",
          distribuir: true,
          ncuotas: cuotasDe(ex), // 0 = todas las pendientes
        });
        if (!ok) fallidos.push(`${ex.tipogasto} (${data.Error || "error"})`);
      } catch {
        fallidos.push(ex.tipogasto);
      }
    }
    if (fallidos.length) {
      showAlert({
        message: `El préstamo #${noprest} se creó, pero no se registraron: ${fallidos.join(
          ", "
        )}. Regístrelas desde Ventas Generales asociando el préstamo.`,
        color: "warning",
      });
    }
  };

  // Cobra los documentos financiados contra el préstamo recién creado. La
  // venta ya está guardada: un fallo aquí solo se avisa (se pueden cobrar
  // después desde Cobro de Matrícula financiados al préstamo).
  const registrarDocumentos = async (noprest) => {
    const fallidos = [];
    for (const doc of documentos) {
      try {
        // Secuencial a propósito: el backend abre una conexión por request.
        // eslint-disable-next-line no-await-in-loop
        const { ok, data } = await post("/api/cobromatricula_guardar", {
          modo: "prestamo",
          noprest: String(noprest),
          chasis: form.chasis,
          valor: doc.valor,
          descripcion: `Venta a crédito préstamo #${noprest}`,
          tipodepago: "",
          descripcionpago: "",
          tipodocumento: doc.tipo,
          financiar: true,
          ncuotas: cuotasDe(doc), // 0 = todas las pendientes
        });
        if (!ok) fallidos.push(`${labelTipo(doc.tipo)} (${data.Error || "error"})`);
      } catch {
        fallidos.push(labelTipo(doc.tipo));
      }
    }
    if (fallidos.length) {
      showAlert({
        message: `El préstamo #${noprest} se creó, pero no se cobraron: ${fallidos.join(
          ", "
        )}. Regístrelos desde Cobro de Matrícula financiados al préstamo.`,
        color: "warning",
      });
    }
  };

  const guardar = async (autorizacionid = "") => {
    if (!chasisValidado || !form.chasis) {
      showAlert({
        message: "No se ha validado el chasis. Selecciónelo de la lista",
        color: "warning",
      });
      setTab(1);
      return;
    }
    const errorExtra =
      validarCamposExtra(seccionesExtra, datosExtra) ||
      validarCamposExtra(seccionesExtraCod, datosExtraCod, "fiador");
    if (errorExtra) {
      showAlert({ message: errorExtra, color: "warning" });
      return;
    }
    // Días fijos de quincena: o ninguno (cada 15 días corridos) o los dos,
    // entre 1 y 31 y distintos.
    if (form.formadepago === "Quincenas" && (form.diaquincena1 || form.diaquincena2)) {
      const d1 = parseInt(form.diaquincena1, 10);
      const d2 = parseInt(form.diaquincena2, 10);
      if (!(d1 >= 1 && d1 <= 31) || !(d2 >= 1 && d2 <= 31) || d1 === d2) {
        showAlert({
          message:
            "Días fijos de quincena: indique los dos días, entre 1 y 31 y distintos (ej. 15 y 30), o déjelos vacíos para cuotas cada 15 días",
          color: "warning",
        });
        return;
      }
    }
    const errorPlan = validarPlanCompletivo(form.completivoplan, completivoVenta);
    if (errorPlan) {
      showAlert({ message: errorPlan, color: "warning" });
      return;
    }
    setGuardando(true);
    try {
      const { ok, status, data } = await post("/api/ventaproducto_guardar", {
        ...form,
        completivoplan: completivoVenta > 0 ? normalizarPlan(form.completivoplan) : [],
        foto,
        autorizacionid,
        datosextra: datosExtra,
        datosextracodeudor: datosExtraCod,
      });
      if (ok) {
        const avisocompletivo = toNum(data.completivo)
          ? ` — Completivo por cobrar: RD$ ${fmtMoney(data.completivo)}`
          : "";
        showAlert({
          message: `${data.mensajeok || "Datos guardados correctamente"} — Préstamo #${
            data.noprest
          }${avisocompletivo}`,
          color: "success",
        });
        // Ventas adicionales y documentos: se registran ANTES del conduce,
        // porque suben factmot.vcuotas y el documento debe salir con la
        // cuota final.
        if (extras.length) await registrarExtras(data.noprest);
        if (documentos.length) await registrarDocumentos(data.noprest);
        // Conduce automático de la venta (mismo documento que la reimpresión
        // del informe de motores facturados; el número de préstamo es la
        // secuencia de factmot). Si falla, se reimprime desde el informe.
        try {
          const det = await post("/api/facturamotor_detalle", { secuencia: data.noprest });
          if (det.ok) imprimirFacturaMotor(det.data);
        } catch {
          /* noop */
        }
        setAutOpen(false);
        limpiarPantalla();
        cargarChasis();
        if (onSaved) onSaved();
        return;
      }
      if (status === 409 && data.requiereAutorizacion) {
        setAutMotivo(data.motivo || "Esta operación requiere autorización");
        setAutDescripcion(data.descripcion || "solicito autorizacion de prestamo");
        setAutIdtext("");
        setAutStatus("");
        setAutComentario("");
        setAutOpen(true);
        return;
      }
      showAlert({ message: data.Error || "Error al guardar", color: "error" });
    } catch {
      showAlert({ message: "Error de red o servidor", color: "error" });
    } finally {
      setGuardando(false);
    }
  };

  // Vista con las ventas adicionales incluidas: al guardar, su valor se suma
  // al capital de las cuotas y a la deuda del préstamo, así que la cuota y la
  // deuda que se le cantan al cliente ya las traen. El préstamo se envía sin
  // ellas: el backend las suma al registrar cada venta.
  // Cada ítem puede repartirse en menos cuotas que el plazo, así que el
  // incremento de la cuota es la suma de valor/cuotas de cada uno (aplica a
  // las primeras cuotas, que son las que los cargan todos).
  const extrasPorCuota = extras.reduce((s, ex) => {
    const n = cuotasDe(ex);
    return n > 0 ? s + ex.valor / n : s;
  }, 0);
  const docsPorCuota = documentos.reduce((s, d) => {
    const n = cuotasDe(d);
    return n > 0 ? s + d.valor / n : s;
  }, 0);
  const cuotaConExtras = toNum(form.valorcuota) + extrasPorCuota + docsPorCuota;
  const deudaConExtras = toNum(form.deudatotal) + totalExtras + totalDocumentos;

  const resumen = useMemo(
    () => [
      { label: "Financia", value: fmtMoney(form.financia) },
      { label: "Valor cuota", value: fmtMoney(cuotaConExtras) },
      { label: "Deuda total", value: fmtMoney(deudaConExtras) },
    ],
    [form.financia, cuotaConExtras, deudaConExtras]
  );

  // ---- Cobro del inicial desde la venta (mismo diálogo que Iniciales,
  // ares4020; el backend lo re-valida) ----
  const puedeCobrarInicial = tienePermisosModulo(roleDetalles, "ares4020");
  const [cobroInicialOpen, setCobroInicialOpen] = useState(false);

  // Relee el inicial cobrado del chasis y recalcula el financiamiento sin
  // tocar las condiciones digitadas (validarChasis las resetea).
  // Devuelve { ok, inicial } o { ok: false, error }.
  const refrescarInicial = async (chasis, { avisar = true } = {}) => {
    const { ok, data } = await post("/api/ventaproducto_validarchasis", { chasis });
    if (!ok) return { ok: false, error: data.Error };
    setChasisValidado(true);
    setAbonosInicial(Array.isArray(data.inicial?.abonos) ? data.inicial.abonos : []);
    const inicial = toNum(data.inicial?.valor);
    setForm((p) =>
      inicial === toNum(p.inicial)
        ? p
        : recalcular({ ...p, inicial: inicial ? String(inicial) : "" })
    );
    if (avisar) {
      showAlert({
        message: `Inicial cobrado del chasis: RD$ ${fmtMoney(
          inicial
        )}. Se recalculó el financiamiento`,
        color: "info",
      });
    }
    return { ok: true, inicial };
  };

  // ---- Borrador ("stand by", ver lib/borradorventa) ----
  // Se guarda lo digitado (cliente, préstamo, fiador, campos adicionales,
  // ventas adicionales y documentos); no lo que está a medio agregar en esas
  // listas. El chasis no queda reservado mientras la venta está en pausa: al
  // retomar se vuelve a validar sin pisar las condiciones digitadas, y si el
  // inicial cobrado del chasis cambió se toma el actual y se recalcula.
  const restaurar = async (d) => {
    detenerPolling();
    const f = { ...FORM_INICIAL, ...(d.form || {}) };
    setForm(f);
    setTipodoc(d.tipodoc || "cedula");
    setTipodocFiador(d.tipodocFiador || "cedula");
    setFoto(d.foto || "");
    setPendiente(toNum(d.pendiente));
    setSolicitudCredito(d.solicitudCredito || null);
    setDatosExtra(d.datosExtra || {});
    setDatosExtraCod(d.datosExtraCod || {});
    setExtras(Array.isArray(d.extras) ? d.extras : []);
    setDocumentos(Array.isArray(d.documentos) ? d.documentos : []);
    setTab(d.tab === 1 ? 1 : 0);
    setChasisValidado(false);
    setAbonosInicial([]);
    setAutOpen(false);
    setAutIdtext("");
    setAutStatus("");
    setAutComentario("");
    if (!f.chasis) return;
    const res = await refrescarInicial(f.chasis, { avisar: false });
    if (!res.ok) {
      showAlert({
        message: `El chasis ${f.chasis} ya no está disponible: ${
          res.error || "no se pudo validar"
        }. Seleccione otro.`,
        color: "warning",
      });
      setForm((p) => ({
        ...p,
        chasis: "",
        marca: "",
        modelo: "",
        colore: "",
        precio: "",
        inicial: "",
        inicialpactado: "",
        completivoplan: [],
        financia: "",
      }));
      return;
    }
    if (res.inicial !== toNum(f.inicial)) {
      showAlert({
        message: `El inicial cobrado del chasis cambió a RD$ ${fmtMoney(
          res.inicial
        )} mientras la venta estaba en pausa; se recalculó el financiamiento`,
        color: "info",
      });
    }
    // Autorización que quedó en curso o aprobada: se retoma la misma.
    const { aut } = d;
    if (aut?.idtext) {
      setAutMotivo(aut.motivo || "");
      setAutDescripcion(aut.descripcion || "");
      setAutIdtext(aut.idtext);
      setAutStatus(aut.status);
      setAutOpen(true);
      if (aut.status === "Progreso") iniciarPolling(aut.idtext);
    }
  };

  const borrador = useBorradorVenta({
    tipo: "credito",
    datos: {
      form,
      tipodoc,
      tipodocFiador,
      foto,
      pendiente,
      solicitudCredito,
      datosExtra,
      datosExtraCod,
      extras,
      documentos,
      tab,
      aut:
        autOpen && autIdtext && (autStatus === "Progreso" || autStatus === "Aprobado")
          ? { idtext: autIdtext, status: autStatus, motivo: autMotivo, descripcion: autDescripcion }
          : null,
    },
    resumen: {
      cliente: `${form.nombres} ${form.apellidos}`.trim() || form.cedula,
      chasis: form.chasis,
    },
    conDatos: [form.cedula, form.nombres, form.apellidos, form.chasis].some((v) =>
      String(v || "").trim()
    ),
    restaurar,
    autoRestaurar: restaurarBorrador,
  });

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      <PageHeader
        title="Venta de Productos / Vehículos"
        subtitle="Registra la venta financiada: cliente, chasis, condiciones del préstamo y fiador"
        cta={
          <Stack direction="row" spacing={1.2}>
            {embedded && (
              <Button startIcon={<ArrowBackIcon />} onClick={onExit} sx={btnOutline(FP.color.navy)}>
                Volver
              </Button>
            )}
            <Button
              startIcon={<RestartAltIcon />}
              onClick={limpiarPantalla}
              sx={btnOutline(FP.color.navy)}
            >
              Nuevo
            </Button>
            <Button
              startIcon={guardando ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
              onClick={() => guardar()}
              disabled={guardando || !config}
              sx={btnSolid(FP.color.navy, FP.color.navySoft)}
            >
              Guardar
            </Button>
          </Stack>
        }
      />

      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <AvisoBorradorVenta
          borrador={borrador.oferta}
          etiqueta="venta a crédito"
          onContinuar={borrador.continuar}
          onDescartar={borrador.descartar}
          sx={{ mb: 2 }}
        />
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{
            mb: 2.5,
            "& .MuiTab-root": { textTransform: "none", fontWeight: 700 },
          }}
        >
          <Tab label="Datos Generales" />
          <Tab label="Datos Préstamo" />
        </Tabs>

        {tab === 0 && (
          <>
            <Box sx={sectionBox}>
              <SectionTitle icon={PersonOutlineIcon} color={FP.color.accentBlue}>
                Cliente
              </SectionTitle>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={3} md={2}>
                  <Stack alignItems="center" spacing={1.2}>
                    <Avatar
                      variant="rounded"
                      src={foto ? `data:image/jpeg;base64,${foto}` : undefined}
                      sx={{
                        width: 110,
                        height: 110,
                        bgcolor: FP.color.surfaceAlt,
                        color: FP.color.textMuted,
                      }}
                    >
                      <PersonOutlineIcon sx={{ fontSize: 46 }} />
                    </Avatar>
                    <input
                      ref={fotoInputRef}
                      type="file"
                      accept="image/*"
                      hidden
                      onChange={seleccionarFoto}
                    />
                    <Button
                      size="small"
                      startIcon={<AddAPhotoOutlinedIcon />}
                      onClick={() => fotoInputRef.current?.click()}
                      sx={btnOutline(FP.color.accentBlue)}
                    >
                      Foto
                    </Button>
                  </Stack>
                </Grid>
                <Grid item xs={12} sm={9} md={10}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <DocumentoField
                        value={form.cedula}
                        tipo={tipodoc}
                        onChangeValor={(v) => setForm((p) => ({ ...p, cedula: v }))}
                        onChangeTipo={setTipodoc}
                        onBlur={buscarPorCedula}
                        sx={fpInputSx}
                      />
                      {/* Consulta a la red: corre sola y en paralelo a
                          buscarPorCedula, nunca la bloquea. */}
                      <AlertaRed cedula={form.cedula} tipo={tipodoc} sx={{ mt: 0.75 }} />
                      {/* Tratamiento de co-deudor: ya garantiza préstamos de otros. */}
                      <AlertaCodeudor cedula={form.cedula} tipo={tipodoc} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Edad"
                        value={form.edad}
                        onChange={setF("edad")}
                        sx={fpInputSx}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        label="Pendiente"
                        value={fmtMoney(pendiente)}
                        InputProps={{
                          readOnly: true,
                          startAdornment: <InputAdornment position="start">RD$</InputAdornment>,
                        }}
                        sx={{
                          ...fpInputSx,
                          "& input": {
                            color: pendiente > 0 ? FP.color.danger : FP.color.textPrimary,
                            fontWeight: 700,
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Nombres"
                        value={form.nombres}
                        onChange={setF("nombres")}
                        sx={fpInputSx}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Apellidos"
                        value={form.apellidos}
                        onChange={setF("apellidos")}
                        sx={fpInputSx}
                      />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <TextField
                        select
                        fullWidth
                        label="Sexo"
                        value={form.sexo}
                        onChange={setF("sexo")}
                        sx={fpInputSx}
                      >
                        <MenuItem value="Femenino">Femenino</MenuItem>
                        <MenuItem value="Masculino">Masculino</MenuItem>
                      </TextField>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <TextField
                        select
                        fullWidth
                        label="Estado civil"
                        value={form.ecivil}
                        onChange={setF("ecivil")}
                        sx={fpInputSx}
                      >
                        <MenuItem value="Casado">Casado</MenuItem>
                        <MenuItem value="Soltero">Soltero</MenuItem>
                      </TextField>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <TextField
                        select
                        fullWidth
                        label="Dependientes"
                        value={form.depend}
                        onChange={setF("depend")}
                        sx={fpInputSx}
                      >
                        {["0", "1", "2", "3", "4", "5", "6", "7"].map((n) => (
                          <MenuItem key={n} value={n}>
                            {n}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <TextField
                        select
                        fullWidth
                        label="Nacionalidad"
                        value={form.nacionalidad}
                        onChange={setF("nacionalidad")}
                        sx={fpInputSx}
                      >
                        <MenuItem value="Dominicano">Dominicano</MenuItem>
                        <MenuItem value="Extranjero">Extranjero</MenuItem>
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Apodo"
                        value={form.apodo}
                        onChange={setF("apodo")}
                        sx={fpInputSx}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Box>

            <Box sx={{ ...sectionBox, mb: 0 }}>
              <SectionTitle icon={PhoneOutlinedIcon} color={FP.color.info}>
                Contacto
              </SectionTitle>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Dirección"
                    value={form.direccion}
                    onChange={setF("direccion")}
                    sx={fpInputSx}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Autocomplete
                    options={provincias}
                    value={form.provincia || null}
                    onChange={(_, v) => setForm((p) => ({ ...p, provincia: v || "" }))}
                    renderInput={(params) => (
                      <TextField {...params} label="Provincia" sx={fpInputSx} />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Teléfono"
                    value={form.telefono}
                    onChange={setF("telefono")}
                    sx={fpInputSx}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Celular"
                    value={form.celular}
                    onChange={setF("celular")}
                    sx={fpInputSx}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Email"
                    value={form.email}
                    onChange={setF("email")}
                    sx={fpInputSx}
                  />
                </Grid>
              </Grid>
            </Box>

            {/* Secciones configurables de la pestaña Datos Generales. */}
            <CamposExtraForm
              secciones={seccionesExtraGenerales}
              valores={datosExtra}
              onChange={(id, v) => setDatosExtra((p) => ({ ...p, [id]: v }))}
              sectionSx={{ ...sectionBox, mt: 2.4, mb: 0 }}
            />
          </>
        )}

        {tab === 1 && (
          <>
            <Box sx={sectionBox}>
              <SectionTitle icon={DirectionsCarFilledOutlinedIcon} color={FP.color.accentBlue}>
                Producto / Vehículo
              </SectionTitle>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Autocomplete
                    freeSolo
                    autoHighlight
                    selectOnFocus
                    handleHomeEndKeys
                    filterOptions={filtrarChasis}
                    options={chasisOpts}
                    loading={loadingChasis}
                    value={form.chasis || null}
                    getOptionLabel={(o) => (typeof o === "string" ? o : o.chasis || "")}
                    isOptionEqualToValue={(o, v) => (o.chasis || o) === (v.chasis || v)}
                    renderOption={(props, o) => (
                      <li {...props} key={o.chasis}>
                        <Box>
                          <Typography sx={{ fontWeight: 700, fontSize: "0.85rem" }}>
                            {o.chasis}
                          </Typography>
                          <Typography sx={{ fontSize: "0.72rem", color: FP.color.textMuted }}>
                            {[o.marca, o.modelo, o.colore].filter(Boolean).join(" · ")}
                          </Typography>
                        </Box>
                      </li>
                    )}
                    onChange={(_, v) => {
                      const ch = typeof v === "string" ? v : v?.chasis || "";
                      if (ch) validarChasis(ch);
                      else {
                        setChasisValidado(false);
                        setAbonosInicial([]);
                        setForm((p) => ({
                          ...p,
                          chasis: "",
                          marca: "",
                          modelo: "",
                          colore: "",
                          precio: "",
                          inicial: "",
                          financia: "",
                          valorcuota: "",
                          deudatotal: "",
                        }));
                      }
                    }}
                    renderInput={(params) => (
                      <TextField {...params} label="Chasis" sx={fpInputSx} />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={8 / 3}>
                  <TextField
                    fullWidth
                    label="Marca"
                    value={form.marca}
                    InputProps={{ readOnly: true }}
                    sx={fpInputSx}
                  />
                </Grid>
                <Grid item xs={12} sm={8 / 3}>
                  <TextField
                    fullWidth
                    label="Modelo"
                    value={form.modelo}
                    InputProps={{ readOnly: true }}
                    sx={fpInputSx}
                  />
                </Grid>
                <Grid item xs={12} sm={8 / 3}>
                  <TextField
                    fullWidth
                    label="Color"
                    value={form.colore}
                    InputProps={{ readOnly: true }}
                    sx={fpInputSx}
                  />
                </Grid>
                {usaVendedor && (
                  <Grid item xs={12} sm={4}>
                    <Autocomplete
                      options={usuarios}
                      value={form.vendedor || null}
                      onChange={(_, v) => setForm((p) => ({ ...p, vendedor: v || "" }))}
                      isOptionEqualToValue={(o, v) => o === v}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Vendedor"
                          sx={fpInputSx}
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                              <InputAdornment position="start">
                                <PersonOutlineIcon
                                  sx={{ fontSize: 18, color: FP.color.textMuted }}
                                />
                              </InputAdornment>
                            ),
                          }}
                        />
                      )}
                    />
                  </Grid>
                )}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    label="Comentario"
                    value={form.comentario}
                    onChange={setF("comentario")}
                    inputProps={{ maxLength: 1000 }}
                  />
                </Grid>
              </Grid>
            </Box>

            <Box sx={sectionBox}>
              <SectionTitle icon={PaymentsOutlinedIcon} color={FP.color.success}>
                Financiamiento
              </SectionTitle>
              <Grid container spacing={2}>
                <Grid item xs={6} sm={3}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Precio"
                    value={form.precio}
                    onChange={setCalc("precio")}
                    sx={fpInputSx}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">RD$</InputAdornment>,
                    }}
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField
                    fullWidth
                    label="Inicial cobrado"
                    value={form.inicial ? fmtMoney(form.inicial) : "0.00"}
                    sx={fpInputSx}
                    InputProps={{
                      readOnly: true,
                      startAdornment: <InputAdornment position="start">RD$</InputAdornment>,
                      // Cobrar (o abonar) el inicial sin salir de la venta.
                      endAdornment: puedeCobrarInicial ? (
                        <InputAdornment position="end">
                          <Tooltip
                            title={
                              chasisValidado
                                ? "Cobrar inicial de este chasis"
                                : "Seleccione primero el chasis"
                            }
                          >
                            <span>
                              <Button
                                size="small"
                                disabled={!chasisValidado}
                                onClick={() => setCobroInicialOpen(true)}
                                sx={{
                                  ...btnOutline(FP.color.success),
                                  py: 0.2,
                                  px: 1,
                                  minWidth: 0,
                                }}
                              >
                                Cobrar
                              </Button>
                            </span>
                          </Tooltip>
                        </InputAdornment>
                      ) : undefined,
                    }}
                    helperText={
                      abonosInicial.length > 1
                        ? `${abonosInicial.length} abonos: ${abonosInicial
                            .map((a) => fmtMoney(a.valor))
                            .join(" + ")}`
                        : undefined
                    }
                  />
                </Grid>
                {/* Metodología de completivos: solo si está activada en la
                    configuración (varinicial.completivoinicial). Con solicitud
                    de crédito vigente para el chasis el pactado viene de ella
                    y es de solo lectura (el backend lo relee al guardar e
                    ignora este valor); sin solicitud se digita aquí. */}
                {config?.completivoinicial === "S" && (
                  <Grid item xs={6} sm={3}>
                    <TextField
                      fullWidth
                      type={pactadoDeSolicitud ? "text" : "number"}
                      label="Inicial pactado"
                      value={pactadoValor}
                      onChange={pactadoDeSolicitud ? undefined : setCalc("inicialpactado")}
                      sx={fpInputSx}
                      InputProps={{
                        readOnly: pactadoDeSolicitud,
                        startAdornment: <InputAdornment position="start">RD$</InputAdornment>,
                      }}
                      helperText={pactadoHelper}
                    />
                  </Grid>
                )}
                <Grid item xs={6} sm={3}>
                  <TextField
                    fullWidth
                    label="Financia"
                    value={form.financia ? fmtMoney(form.financia) : "0.00"}
                    sx={fpInputSx}
                    InputProps={{
                      readOnly: true,
                      startAdornment: <InputAdornment position="start">RD$</InputAdornment>,
                    }}
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Plazo (cuotas)"
                    value={form.plazo}
                    onChange={setCalc("plazo")}
                    sx={fpInputSx}
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField
                    select
                    fullWidth
                    label="Forma de pago"
                    value={form.formadepago}
                    onChange={setCalc("formadepago")}
                    sx={fpInputSx}
                  >
                    {formasdepago.map((f) => (
                      <MenuItem key={f} value={f}>
                        {f}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Interés (mensual)"
                    value={form.interes}
                    onChange={setCalc("interes")}
                    sx={fpInputSx}
                    InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <CampoCuota
                    editable={redondeo}
                    cuota={form.valorcuota ? toNum(form.valorcuota) : null}
                    valorMostrado={form.valorcuota ? fmtMoney(cuotaConExtras) : "0.00"}
                    financia={form.financia}
                    plazo={form.plazo}
                    formadepago={form.formadepago}
                    onInteres={(interes) => setForm((p) => recalcular({ ...p, interes }))}
                    helperEdicion={
                      totalExtras > 0 || totalDocumentos > 0
                        ? "Cuota sin ventas adicionales ni documentos; el interés se recalcula"
                        : undefined
                    }
                    helperText={
                      (totalExtras > 0 || totalDocumentos > 0) && form.valorcuota
                        ? "Incluye " +
                          [
                            totalExtras > 0
                              ? `RD$ ${fmtMoney(extrasPorCuota)} de ventas adicionales`
                              : "",
                            totalDocumentos > 0
                              ? `RD$ ${fmtMoney(docsPorCuota)} de documentos`
                              : "",
                          ]
                            .filter(Boolean)
                            .join(" y ")
                        : undefined
                    }
                    sx={fpInputSx}
                  />
                </Grid>
                {elegirPrimeraCuota && (
                  <Grid item xs={6} sm={3}>
                    <TextField
                      fullWidth
                      type="date"
                      label="Fecha primera cuota"
                      value={form.fechaprimeracuota}
                      onChange={(e) =>
                        setForm((p) => ({ ...p, fechaprimeracuota: e.target.value }))
                      }
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ min: mananaISO(), max: unMesISO() }}
                      sx={fpInputSx}
                      helperText="Vacía = automática; hasta un mes"
                    />
                  </Grid>
                )}
                {form.formadepago === "Dias" && (
                  <Grid item xs={12} sm={6} sx={{ display: "flex", alignItems: "center" }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={form.excluirdomingos}
                          onChange={(e) =>
                            setForm((p) => ({ ...p, excluirdomingos: e.target.checked }))
                          }
                        />
                      }
                      label="Excluir domingos (las cuotas saltan al lunes)"
                    />
                  </Grid>
                )}
                {form.formadepago === "Quincenas" && (
                  <>
                    <Grid item xs={6} sm={3}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Día fijo 1"
                        value={form.diaquincena1}
                        onChange={(e) => setForm((p) => ({ ...p, diaquincena1: e.target.value }))}
                        sx={fpInputSx}
                        inputProps={{ min: 1, max: 31 }}
                        helperText="Ej: 15. Vacíos = cada 15 días"
                      />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Día fijo 2"
                        value={form.diaquincena2}
                        onChange={(e) => setForm((p) => ({ ...p, diaquincena2: e.target.value }))}
                        sx={fpInputSx}
                        inputProps={{ min: 1, max: 31 }}
                        helperText="Ej: 30 (en febrero cae al 28/29)"
                      />
                    </Grid>
                  </>
                )}
                {/* Resultado alineado a la derecha, debajo de Valor cuotas,
                    con o sin las opciones de calendario a la izquierda. */}
                <Grid item xs={6} sm={3} sx={{ ml: "auto" }}>
                  <TextField
                    fullWidth
                    label="Deuda total"
                    value={form.deudatotal ? fmtMoney(deudaConExtras) : "0.00"}
                    helperText={
                      (totalExtras > 0 || totalDocumentos > 0) && form.deudatotal
                        ? "Incluye " +
                          [
                            totalExtras > 0
                              ? `RD$ ${fmtMoney(totalExtras)} de ventas adicionales`
                              : "",
                            totalDocumentos > 0
                              ? `RD$ ${fmtMoney(totalDocumentos)} de documentos`
                              : "",
                          ]
                            .filter(Boolean)
                            .join(" y ")
                        : undefined
                    }
                    sx={fpInputSx}
                    InputProps={{
                      readOnly: true,
                      startAdornment: <InputAdornment position="start">RD$</InputAdornment>,
                    }}
                  />
                </Grid>
                {/* Plan del completivo: el saldo del inicial pactado repartido
                    en partes con fecha; en Pagos se propone cobrar solo lo
                    vencido. Sin plan, vence todo el día de la venta. */}
                {completivoVenta > 0 && (
                  <Grid item xs={12}>
                    <PlanCompletivoEditor
                      completivo={completivoVenta}
                      plan={form.completivoplan}
                      onChange={(plan) => setForm((p) => ({ ...p, completivoplan: plan }))}
                    />
                  </Grid>
                )}
              </Grid>
            </Box>

            <Box sx={{ ...sectionBox, mb: 0 }}>
              <SectionTitle icon={AssignmentIndOutlinedIcon} color={FP.color.danger}>
                Fiador
              </SectionTitle>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Nombre fiador"
                    value={form.fiador}
                    onChange={setF("fiador")}
                    sx={fpInputSx}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <DocumentoField
                    label="Documento fiador"
                    value={form.fiadorced}
                    tipo={tipodocFiador}
                    onChangeValor={(v) => setForm((p) => ({ ...p, fiadorced: v }))}
                    onChangeTipo={setTipodocFiador}
                    onBlur={() => {
                      if (!documentoValido(form.fiadorced, tipodocFiador)) return;
                      buscarFiadorGenerales();
                      buscarFiadorExtra();
                    }}
                    sx={fpInputSx}
                  />
                  <AlertaCodeudor cedula={form.fiadorced} tipo={tipodocFiador} quien="fiador" />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Teléfono fiador"
                    value={form.fiadortel}
                    onChange={setF("fiadortel")}
                    sx={fpInputSx}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Dirección fiador"
                    value={form.fiadordir}
                    onChange={setF("fiadordir")}
                    sx={fpInputSx}
                  />
                </Grid>
              </Grid>
            </Box>

            {/* Secciones de persona del fiador (Campos Adicionales marcadas
                "También al fiador / co-deudor"). */}
            <CamposExtraForm
              secciones={seccionesExtraCod}
              valores={datosExtraCod}
              onChange={(id, v) => setDatosExtraCod((p) => ({ ...p, [id]: v }))}
              sectionSx={{ ...sectionBox, mt: 2.4, mb: 0 }}
              sufijo="Fiador"
            />

            {/* Secciones configurables de la pestaña Datos Préstamo. */}
            <CamposExtraForm
              secciones={seccionesExtraPrestamo}
              valores={datosExtra}
              onChange={(id, v) => setDatosExtra((p) => ({ ...p, [id]: v }))}
              sectionSx={{ ...sectionBox, mt: 2.4, mb: 0 }}
            />
          </>
        )}

        {/* Resumen fijo del financiamiento */}
        <Stack
          direction="row"
          spacing={3}
          alignItems="center"
          sx={{ mt: 2.5, px: 1 }}
          flexWrap="wrap"
        >
          {resumen.map((r) => (
            <Typography key={r.label} sx={{ fontSize: "0.86rem", color: FP.color.textSecondary }}>
              {r.label}: <b style={{ color: FP.color.textPrimary }}>RD$ {r.value}</b>
            </Typography>
          ))}
          {totalExtras > 0 && (
            <Typography sx={{ fontSize: "0.86rem", color: FP.color.textSecondary }}>
              Adicionales:{" "}
              <b style={{ color: FP.color.textPrimary }}>RD$ {fmtMoney(totalExtras)}</b>
            </Typography>
          )}
          {totalDocumentos > 0 && (
            <Typography sx={{ fontSize: "0.86rem", color: FP.color.textSecondary }}>
              Documentos:{" "}
              <b style={{ color: FP.color.textPrimary }}>RD$ {fmtMoney(totalDocumentos)}</b>
            </Typography>
          )}
          {ventasConfig && (
            <Button
              size="small"
              startIcon={<SellOutlinedIcon />}
              onClick={() => setExtrasOpen(true)}
              sx={btnOutline(FP.color.accentBlue)}
            >
              Ventas adicionales{extras.length ? ` (${extras.length})` : ""}
            </Button>
          )}
          {puedeDocumentos && (
            <Button
              size="small"
              startIcon={<BadgeOutlinedIcon />}
              onClick={() => setDocsOpen(true)}
              sx={btnOutline(FP.color.accentBlue)}
            >
              Documentos{documentos.length ? ` (${documentos.length})` : ""}
            </Button>
          )}
        </Stack>
      </Box>

      {/* Cobro del inicial con el cliente y el chasis de la venta */}
      {puedeCobrarInicial && (
        <CobroInicialDialog
          open={cobroInicialOpen}
          onClose={() => setCobroInicialOpen(false)}
          cerrarAlGuardar
          prefill={{
            cedula: form.cedula,
            tipodoc,
            nombres: form.nombres,
            apellidos: form.apellidos,
            chasis: form.chasis,
            // Sugerido: lo que falta del inicial pactado, si lo hay.
            valor: Math.max(0, toNum(form.inicialpactado) - toNum(form.inicial)),
          }}
          onSaved={() => {
            if (form.chasis) refrescarInicial(form.chasis);
          }}
        />
      )}

      {/* Dialog de autorización */}
      <Dialog
        open={autOpen}
        onClose={() => {}}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, display: "flex", alignItems: "center", gap: 1.2 }}>
          <GppMaybeOutlinedIcon sx={{ color: FP.color.warning }} />
          Se requiere autorización
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: "0.9rem", color: FP.color.textSecondary, mb: 2 }}>
            {autMotivo}
          </Typography>

          {autStatus === "" && (
            <Typography sx={{ fontSize: "0.84rem", color: FP.color.textMuted }}>
              Haz clic en &quot;Solicitar autorización&quot; para enviar la petición al
              administrador.
            </Typography>
          )}
          {autStatus === "Progreso" && (
            <Stack direction="row" spacing={1.4} alignItems="center">
              <CircularProgress size={18} />
              <Typography sx={{ fontSize: "0.9rem", color: FP.color.danger, fontWeight: 700 }}>
                Esperando aprobación…
              </Typography>
            </Stack>
          )}
          {autStatus === "Aprobado" && (
            <Stack direction="row" spacing={1.4} alignItems="center">
              <CheckCircleOutlineIcon sx={{ color: FP.color.success }} />
              <Typography sx={{ fontSize: "0.9rem", color: FP.color.success, fontWeight: 700 }}>
                Autorización aprobada. Ya puedes guardar la venta.
              </Typography>
            </Stack>
          )}
          {autStatus === "Cancelado" && (
            <Typography sx={{ fontSize: "0.9rem", color: FP.color.danger, fontWeight: 700 }}>
              Autorización declinada{autComentario ? ` — mensaje: ${autComentario}` : ""}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={cancelarAutorizacion} sx={btnGhost}>
            Cerrar
          </Button>
          {autStatus === "" && (
            <Button
              disableElevation
              onClick={solicitarAutorizacion}
              sx={btnSolid(FP.color.warning, "#B45309")}
            >
              Solicitar autorización
            </Button>
          )}
          {autStatus === "Aprobado" && (
            <Button
              disableElevation
              startIcon={guardando ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
              disabled={guardando}
              onClick={() => guardar(autIdtext)}
              sx={btnSolid(FP.color.success, FP.color.successDark)}
            >
              Guardar con autorización
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Dialog de ventas adicionales (GPS, seguros) */}
      <Dialog
        open={extrasOpen}
        onClose={() => setExtrasOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, display: "flex", alignItems: "center", gap: 1.2 }}>
          <SellOutlinedIcon sx={{ color: FP.color.accentBlue }} />
          Ventas Adicionales
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: "0.84rem", color: FP.color.textSecondary, mb: 2 }}>
            Se registran como ventas a crédito del préstamo y su valor se reparte entre las cuotas:
            el cliente las paga junto con cada cuota.
          </Typography>
          <Grid container spacing={1.4} alignItems="center">
            <Grid item xs={12} sm={4}>
              <TextField
                select
                fullWidth
                size="small"
                label="Tipo Ingreso"
                value={extraCat}
                onChange={(e) => setExtraCat(e.target.value)}
                sx={fpInputSx}
              >
                {(ventasConfig?.categorias || []).map((c) => (
                  <MenuItem key={c.id} value={c.categoria}>
                    {c.categoria}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                size="small"
                label="Valor"
                value={extraValor}
                onChange={(e) => setExtraValor(e.target.value)}
                placeholder="0.00"
                inputProps={{ style: { textAlign: "right" } }}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={6} sm={2}>
              <TextField
                fullWidth
                size="small"
                label="GPS #"
                required={extraGpsObligatorio}
                value={extraGps}
                onChange={(e) => setExtraGps(e.target.value)}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={6} sm={2}>
              <TextField
                fullWidth
                type="number"
                size="small"
                label="Cuotas"
                value={extraCuotas}
                onChange={(e) => setExtraCuotas(e.target.value)}
                placeholder={form.plazo ? String(form.plazo) : ""}
                inputProps={{ min: 1, max: toNum(form.plazo) || undefined }}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <Button startIcon={<AddIcon />} onClick={agregarExtra} sx={btnOutline(FP.color.navy)}>
                Agregar
              </Button>
            </Grid>
          </Grid>
          {extras.length > 0 && (
            <Stack spacing={0.6} sx={{ mt: 2 }}>
              {extras.map((ex, idx) => (
                <Stack
                  // eslint-disable-next-line react/no-array-index-key
                  key={`${ex.tipogasto}-${idx}`}
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{
                    px: 1.4,
                    py: 0.6,
                    borderRadius: 1.6,
                    border: `1px solid ${FP.color.border}`,
                  }}
                >
                  <Typography
                    sx={{ fontWeight: 700, fontSize: "0.86rem", minWidth: 0, flex: 1, mr: 1 }}
                  >
                    {ex.tipogasto}
                    {ex.gps ? ` · GPS ${ex.gps}` : ""}
                  </Typography>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ flexShrink: 0 }}>
                    <Typography sx={{ fontWeight: 800, fontSize: "0.86rem", whiteSpace: "nowrap" }}>
                      RD$ {fmtMoney(ex.valor)}
                      {cuotasDe(ex) > 0
                        ? ` · ${fmtMoney(ex.valor / cuotasDe(ex))} × ${cuotasDe(ex)}`
                        : ""}
                    </Typography>
                    <IconButton
                      size="small"
                      aria-label="Quitar venta adicional"
                      onClick={() => eliminarExtra(idx)}
                      sx={{ color: FP.color.danger }}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </Stack>
              ))}
              <Typography sx={{ fontSize: "0.8rem", color: FP.color.textMuted, mt: 0.6 }}>
                Total adicional: RD$ {fmtMoney(totalExtras)}
                {extrasPorCuota > 0
                  ? ` — la cuota sube RD$ ${fmtMoney(extrasPorCuota)}, de RD$ ${fmtMoney(
                      form.valorcuota
                    )} a RD$ ${fmtMoney(cuotaConExtras)}`
                  : ""}
                {extras.some((ex) => cuotasDe(ex) < toNum(form.plazo))
                  ? " (solo mientras duren las cuotas indicadas)"
                  : ""}
              </Typography>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={() => setExtrasOpen(false)}
            sx={btnSolid(FP.color.navy, FP.color.navySoft)}
          >
            Listo
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de costo de documentos financiado en cuotas */}
      <Dialog
        open={docsOpen}
        onClose={() => setDocsOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, display: "flex", alignItems: "center", gap: 1.2 }}>
          <BadgeOutlinedIcon sx={{ color: FP.color.accentBlue }} />
          Costo de Documentos
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: "0.84rem", color: FP.color.textSecondary, mb: 2 }}>
            Se registran como cobros de documentos del vehículo (matrícula, placa...) y su valor se
            reparte entre las cuotas: el cliente los paga junto con cada cuota.
          </Typography>
          <Grid container spacing={1.4} alignItems="center">
            <Grid item xs={12} sm={4}>
              <TextField
                select
                fullWidth
                size="small"
                label="Documento"
                value={docTipo}
                onChange={(e) => elegirDocTipo(e.target.value)}
                helperText={
                  docTarifaPropuesta > 0 ? `Tarifa: RD$ ${fmtMoney(docTarifaPropuesta)}` : undefined
                }
                sx={fpInputSx}
              >
                {tiposDocumento.map((t) => (
                  <MenuItem key={t.value} value={t.value}>
                    {t.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                size="small"
                label="Valor"
                value={docValor}
                onChange={(e) => setDocValor(e.target.value)}
                placeholder="0.00"
                inputProps={{ style: { textAlign: "right" } }}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={6} sm={2}>
              <TextField
                fullWidth
                type="number"
                size="small"
                label="Cuotas"
                value={docCuotas}
                onChange={(e) => setDocCuotas(e.target.value)}
                placeholder={form.plazo ? String(form.plazo) : ""}
                inputProps={{ min: 1, max: toNum(form.plazo) || undefined }}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <Button
                startIcon={<AddIcon />}
                onClick={agregarDocumento}
                sx={btnOutline(FP.color.navy)}
              >
                Agregar
              </Button>
            </Grid>
          </Grid>
          {documentos.length > 0 && (
            <Stack spacing={0.6} sx={{ mt: 2 }}>
              {documentos.map((doc, idx) => (
                <Stack
                  // eslint-disable-next-line react/no-array-index-key
                  key={`${doc.tipo}-${idx}`}
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{
                    px: 1.4,
                    py: 0.6,
                    borderRadius: 1.6,
                    border: `1px solid ${FP.color.border}`,
                  }}
                >
                  <Typography
                    sx={{ fontWeight: 700, fontSize: "0.86rem", minWidth: 0, flex: 1, mr: 1 }}
                  >
                    {labelTipo(doc.tipo)}
                  </Typography>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ flexShrink: 0 }}>
                    <Typography sx={{ fontWeight: 800, fontSize: "0.86rem", whiteSpace: "nowrap" }}>
                      RD$ {fmtMoney(doc.valor)}
                      {cuotasDe(doc) > 0
                        ? ` · ${fmtMoney(doc.valor / cuotasDe(doc))} × ${cuotasDe(doc)}`
                        : ""}
                    </Typography>
                    <IconButton
                      size="small"
                      aria-label="Quitar documento"
                      onClick={() => eliminarDocumento(idx)}
                      sx={{ color: FP.color.danger }}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </Stack>
              ))}
              <Typography sx={{ fontSize: "0.8rem", color: FP.color.textMuted, mt: 0.6 }}>
                Total documentos: RD$ {fmtMoney(totalDocumentos)}
                {docsPorCuota > 0
                  ? ` — la cuota sube RD$ ${fmtMoney(docsPorCuota)}, de RD$ ${fmtMoney(
                      form.valorcuota
                    )} a RD$ ${fmtMoney(cuotaConExtras)}`
                  : ""}
                {documentos.some((doc) => cuotasDe(doc) < toNum(form.plazo))
                  ? " (solo mientras duren las cuotas indicadas)"
                  : ""}
              </Typography>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={() => setDocsOpen(false)}
            sx={btnSolid(FP.color.navy, FP.color.navySoft)}
          >
            Listo
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
